import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  Pressable,
  ScrollView,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { substituteText } from '../../utils/substituteText';
import { Paprika, PaprikaSpacing, PaprikaTypography, PaprikaRadius, PaprikaShadows } from '../../styles/theme';
import {
  findSubstitutionsForPantry,
  ownsSubstitute,
} from '../../services/ingredientSubstitutionService';
import type { CanonicalIngredientId } from '../../utils/ingredientVocabulary';
import { SubOption } from './SubOption';

const isWeb = Platform.OS === 'web';

/** Stable identity, so the memo above does not rebuild on every render. */
const EMPTY_OWNED: ReadonlySet<string> = new Set();

export interface ChosenSubstitute {
  name: string;
  ratio?: string;
  notes?: string;
}

interface SubstitutionSheetProps {
  visible: boolean;
  ingredientName: string;
  /** name of the currently-applied substitute for this ingredient, if any */
  selectedSubName?: string;
  /**
   * Canonical ids of everything in the pantry right now.
   *
   * Substitutes the user owns lead the list. This was impossible before the
   * vocabulary: substitutes were free text, so 192 of the 275 single-ingredient
   * names had no identity and "do I own one?" had no answer.
   */
  ownedIds?: ReadonlySet<CanonicalIngredientId>;
  onClose: () => void;
  onSelect: (sub: ChosenSubstitute) => void;
}

/**
 * Substitution picker. On mobile it is a bottom sheet above a scrim; on web it
 * is a centered dialog. Content mirrors the desktop SubstitutionPanel: label →
 * ingredient name (serif) → subtext → curated SubOption list.
 */
export const SubstitutionSheet: React.FC<SubstitutionSheetProps> = ({
  visible,
  ingredientName,
  selectedSubName,
  ownedIds,
  onClose,
  onSelect,
}) => {
  const { t, i18n } = useTranslation();
  const owned = ownedIds ?? EMPTY_OWNED;
  const result = React.useMemo(
    () => findSubstitutionsForPantry(ingredientName, owned, i18n.language),
    [ingredientName, owned, i18n.language]
  );
  const displayName = result.found ? result.ingredient : ingredientName;

  return (
    <Modal visible={visible} transparent animationType={isWeb ? 'fade' : 'slide'} onRequestClose={onClose}>
      <Pressable style={isWeb ? styles.overlayWeb : styles.overlay} onPress={onClose}>
        <Pressable testID="substitution-sheet" style={isWeb ? styles.panelWeb : styles.sheet} onPress={(e) => e.stopPropagation()}>
          {!isWeb && (
            <View style={styles.handleContainer}>
              <View style={styles.handle} />
            </View>
          )}

          {/* Close (web) */}
          {isWeb && (
            <TouchableOpacity style={styles.closeButton} onPress={onClose} hitSlop={8}>
              <Feather name="x" size={20} color={Paprika.textSecondary} />
            </TouchableOpacity>
          )}

          <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.content}>
            <Text style={styles.eyebrow}>{t('recipeDetail.replace', 'REMPLACER')}</Text>
            <Text style={styles.ingredientName}>
              {displayName.charAt(0).toUpperCase() + displayName.slice(1)}
            </Text>
            <Text style={styles.subtext}>
              {t('recipeDetail.substituteHint', 'Ajustez la quantité selon la note.')}
            </Text>

            {result.found ? (
              <View style={styles.list}>
                {result.substitutes.map((sub, i) => (
                  <React.Fragment key={`${sub.name}-${i}`}>
                    {/* One divider, at the boundary between what you own and
                        what you do not. Ordering, not filtering: a substitute
                        you lack is still worth seeing, it just should not be
                        the first thing you read. */}
                    {i === result.ownedCount && result.ownedCount > 0 && (
                      <Text style={styles.ownedDivider}>
                        {t('recipeDetail.otherOptions', 'Autres options')}
                      </Text>
                    )}
                    <SubOption
                      {...substituteText(sub, i18n.language)}
                      owned={ownsSubstitute(sub, owned)}
                      selected={selectedSubName === sub.name}
                      onUse={() => onSelect({ name: sub.name, ratio: sub.ratio, notes: sub.notes })}
                    />
                  </React.Fragment>
                ))}
              </View>
            ) : (
              <View style={styles.empty}>
                <Feather name="refresh-cw" size={28} color={Paprika.textMuted} />
                <Text style={styles.emptyText}>
                  {t('recipeDetail.noSubstitutes', 'Aucune alternative courante pour cet ingrédient.')}
                </Text>
              </View>
            )}

            <Text testID="substitution-footer-note" style={styles.footerNote}>
              {t('recipeDetail.substituteFooter', 'Le choix met à jour votre liste d’épicerie.')}
            </Text>
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  ownedDivider: {
    ...PaprikaTypography.labelSmall,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    color: Paprika.textMuted,
    marginTop: PaprikaSpacing.sm,
    marginBottom: PaprikaSpacing.xs,
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(24, 16, 8, 0.34)',
    justifyContent: 'flex-end',
  },
  overlayWeb: {
    flex: 1,
    backgroundColor: 'rgba(24, 16, 8, 0.34)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: PaprikaSpacing.lg,
  },
  sheet: {
    backgroundColor: Paprika.cardBg,
    borderTopLeftRadius: PaprikaRadius.xl,
    borderTopRightRadius: PaprikaRadius.xl,
    maxHeight: '82%',
    // See `content` — the framing inset lives there so panelWeb gets it too.
    paddingBottom: PaprikaSpacing.sm,
  },
  panelWeb: {
    backgroundColor: Paprika.cardBg,
    borderRadius: 20,
    width: '100%',
    maxWidth: 460,
    maxHeight: '82%',
    paddingTop: PaprikaSpacing.lg,
    ...PaprikaShadows.modal,
  },
  handleContainer: {
    alignItems: 'center',
    paddingVertical: PaprikaSpacing.sm,
  },
  handle: {
    width: 36,
    height: 4,
    backgroundColor: Paprika.border,
    borderRadius: 2,
  },
  closeButton: {
    position: 'absolute',
    top: PaprikaSpacing.md,
    right: PaprikaSpacing.md,
    zIndex: 2,
    padding: 4,
  },
  content: {
    paddingHorizontal: PaprikaSpacing.lg,
    paddingTop: PaprikaSpacing.xs,
    // Same omission CookLog had: the bottom inset sat on the native `sheet`
    // only, so on web the footer note ended flush on the panel edge. On the
    // content style both variants inherit it.
    paddingBottom: PaprikaSpacing.lg,
    gap: PaprikaSpacing.xs,
  },
  eyebrow: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textMuted,
    letterSpacing: 0.6,
  },
  ingredientName: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 26,
    lineHeight: 32,
    color: Paprika.textPrimary,
  },
  subtext: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    marginBottom: PaprikaSpacing.sm,
  },
  list: {
    gap: PaprikaSpacing.sm,
  },
  empty: {
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
    paddingVertical: PaprikaSpacing.xl,
  },
  emptyText: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textMuted,
    textAlign: 'center',
  },
  footerNote: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
    textAlign: 'center',
    marginTop: PaprikaSpacing.md,
  },
});

export default SubstitutionSheet;
